import React from 'react';
import { certificateDownloadUrl } from '../../services/certificateApi';

const statusLabel = {
  pendiente: 'Pendiente',
  procesando: 'Procesando',
  exitoso: 'Generada',
  fallido: 'Fallida',
  cancelado: 'Cancelada',
};

export const CertificateBatchProgress = ({
  batch,
  running = false,
  error = '',
  onResume,
  onRetry,
  onPublish,
  onCancel,
  onCancelIssuance = () => {},
  onClear,
}) => {
  if (!batch && !running && !error) return null;

  const total = Number(batch?.total) || 0;
  const successful = Number(batch?.exitosos) || 0;
  const failed = Number(batch?.fallidos) || 0;
  const pending = Number(batch?.pendientes) || 0;
  const cancelled = Number(batch?.cancelados) || 0;
  const completed = Math.min(total, successful + failed);
  const students = Array.isArray(batch?.alumnos) ? batch.alumnos : [];
  const publicationState = batch?.estado_publicacion || 'borrador';
  const readyForReview = Boolean(
    batch &&
      batch.estado === 'completado' &&
      publicationState === 'borrador' &&
      total > 0 &&
      successful === total &&
      failed === 0,
  );
  const batchDownloadUrl = batch?.id_lote
    ? certificateDownloadUrl(
        `/api/certificados/lotes/${batch.id_lote}/descargar`,
      )
    : '#';

  return (
    <section
      className="certificate-wizard-card certificate-batch-progress"
      aria-labelledby="certificate-batch-progress-title"
    >
      <div className="certificate-wizard-card-heading">
        <span aria-hidden="true">5</span>
        <div>
          <h2 id="certificate-batch-progress-title">Publicación</h2>
          <p>
            Descarga los archivos definitivos y publícalos después de recibir
            la aprobación del cliente.
          </p>
        </div>
      </div>

      {batch && (
        <>
          <div className="certificate-batch-summary">
            <strong>
              {successful} {successful === 1 ? 'exitosa' : 'exitosas'}
            </strong>
            <strong>
              {failed} {failed === 1 ? 'fallida' : 'fallidas'}
            </strong>
            <span>
              {pending} {pending === 1 ? 'pendiente' : 'pendientes'}
            </span>
            {cancelled > 0 && (
              <span>
                {cancelled} {cancelled === 1 ? 'cancelada' : 'canceladas'}
              </span>
            )}
          </div>
          <progress
            aria-label="Progreso del lote"
            max={Math.max(total, 1)}
            value={completed}
          />
          <p className="certificate-batch-state" role="status">
            {running
              ? 'Procesando constancias...'
              : publicationState === 'publicado'
                ? 'El lote ya está publicado y visible para los estudiantes.'
                : publicationState === 'cancelado'
                  ? 'El lote fue cancelado y sus códigos QR ya no permiten abrir el PDF.'
                  : readyForReview
                    ? 'Las constancias están listas para revisión y todavía no son visibles para los estudiantes.'
                    : 'Estado de generación: ' + (batch.estado || 'procesando')}
          </p>
        </>
      )}

      {!batch && running && (
        <p className="certificate-batch-state" role="status">
          Creando el lote...
        </p>
      )}

      {error && (
        <p className="certificate-wizard-status" role="alert">
          {error}
        </p>
      )}

      {students.length > 0 && (
        <ul className="certificate-batch-students">
          {students.map((student) => (
            <li key={student.id_lote_alumno || student.id_alumno}>
              <span>{student.nombre_alumno}</span>
              <div className="certificate-batch-student-actions">
                <strong>{statusLabel[student.estado] || student.estado}</strong>
                {student.estado === 'exitoso' && student.id_emision && (
                  <>
                    <a
                      href={certificateDownloadUrl(
                        `/api/certificados/emisiones/${student.id_emision}/descargar`,
                      )}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Descargar PDF con QR
                    </a>
                    {readyForReview && students.length - cancelled > 1 && (
                      <button
                        type="button"
                        className="danger-link"
                        onClick={() => onCancelIssuance(student)}
                      >
                        Quitar constancia
                      </button>
                    )}
                  </>
                )}
              </div>
              {student.estado === 'fallido' && student.mensaje_error && (
                <small>{student.mensaje_error}</small>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="certificate-wizard-actions certificate-batch-actions">
        {(pending > 0 || batch?.estado === 'procesando') && !running && (
          <button type="button" onClick={onResume}>
            Reanudar lote
          </button>
        )}
        {failed > 0 && !running && (
          <button type="button" onClick={onRetry}>
            Reintentar fallidas
          </button>
        )}
        {readyForReview && !running && (
          <>
            <a className="secondary" href={batchDownloadUrl}>
              Descargar ZIP para revisión
            </a>
            <button type="button" onClick={onPublish}>
              Publicar para estudiantes
            </button>
            <button type="button" className="danger" onClick={onCancel}>
              Cancelar lote
            </button>
          </>
        )}
        {batch && !running && (
          <button type="button" className="secondary" onClick={onClear}>
            Crear otro lote
          </button>
        )}
      </div>
    </section>
  );
};
